/**
 * The number inside a formatted figure, so a flash can tell which way it went.
 *
 * The cards hold their values already formatted — `$158K`, `18.2K`, `+12.5%` —
 * and the flash sees only the rendered text, so the direction has to be read
 * back out of it. Anything that is not one plain number is left alone and
 * flashes without a direction: a ratio like `0/0`, a dash, and above all the
 * subscript prices the formatter writes for tiny numbers (`0.0₄16`), which
 * would otherwise be read as `0.0`.
 */

const MAGNITUDE: Record<string, number> = {
  k: 1e3,
  m: 1e6,
  b: 1e9,
  t: 1e12,
}

/** Leading currency or label, one number, an optional magnitude, an optional %. */
const ONE_NUMBER = /^[^\d+-]*([+-]?\d+(?:[.,]\d+)?)\s*([kmbt])?%?$/i

export const readNumber = (text: string | null | undefined) => {
  if (!text) {
    return undefined
  }

  const found = ONE_NUMBER.exec(text.trim())
  if (!found) {
    return undefined
  }

  const value = Number(found[1]!.replace(',', '.'))
  if (!Number.isFinite(value)) {
    return undefined
  }

  const magnitude = found[2]?.toLowerCase()
  return magnitude ? value * MAGNITUDE[magnitude]! : value
}

export type FlashDirection = 'up' | 'down' | undefined

/** Which way a figure moved, when both sides read as one number. */
export const directionOf = (
  before: string | null | undefined,
  after: string | null | undefined,
): FlashDirection => {
  const from = readNumber(before)
  const to = readNumber(after)
  if (from === undefined || to === undefined || from === to) {
    return undefined
  }
  return to > from ? 'up' : 'down'
}
